<template>
    <fieldset aria-describedby="content-info-text">
        <legend
            :id="uniqueId"
            class="legendelement"
        >
            {{ label }}
        </legend>
        <div class="search-container">
            <div class="search-field-container">
                <div class="input-group search-group">
                    <input
                        v-model="frequencies"
                        type="text"
                        class="search"
                        :placeholder="placeholder"
                        :aria-label="inputAriaLabel"
                        :aria-describedby="searchValidationErrorMessage != null ? 'validationerror' : null"
                        :aria-invalid="searchValidationErrorMessage != null ? 'true' : null"
                        :class="{ 'error': searchValidationErrorMessage != null }"
                        @keydown.enter.prevent="search"
                        @input="validateInput"
                        @keydown.enter="search"
                    >
                    <button
                        v-if="frequencies.length > 0"
                        class="closebutton"
                        :aria-label="crossIconAlt"
                        @click="emptyResults"
                    >
                        <img
                            class="close-icon"
                            :src="crossIcon"
                            aria-hidden="true"
                        >
                    </button>
                    <input
                        class="searchbutton"
                        type="button"
                        role="button"
                        :aria-label="searchLabel"
                        @click="search"
                    >
                </div>
                <div
                    v-if="searchValidationErrorMessage != null"
                    class="searcherrors"
                    role="alert"
                >
                    <span
                        id="validationerror"
                        class="error-label"
                        aria-live="polite"
                    >{{ searchValidationErrorMessage }}</span>
                </div>
            </div>
        </div>
    </fieldset>
</template>

<script lang="ts">
    import { Component, Vue, Prop } from "vue-facing-decorator"
    import RadiomikrofonitaajuudetService from "@/services/RadiomikrofonitaajuudetService"
    import { store } from '@/store/store'

    @Component
    export default class TextField extends Vue {
        @Prop({ type: String }) readonly label: string | undefined
        @Prop({ type: String }) readonly placeholder: string | undefined
        // input field value
        frequencies: string = ""
        uniqueId: string = ""
        pageName: string = "Radiomikrofonitaajuudet"
        radiomikrofonitaajuudetService: RadiomikrofonitaajuudetService = new RadiomikrofonitaajuudetService()

        created() {
            this.uniqueId = "id-" + Math.random().toString(16).slice(2)
        }

        deactivated() {
            this.emptyResults()
        }

        get crossIcon(): string {
            return store.getters.contentServiceUrl + "/Content/icons/svg-blue/ruksi.svg" + "?" + store.getters.contentServiceTimeStamp
        }

        get crossIconAlt(): string | undefined {
            return this.$t("App.SearchAddress.ClearSearchfield")
        }

        emptyResults(): void {
            this.frequencies = ""
            store.dispatch("setFrequencySearchData", {
                lat: store.getters.frequencySearchData.lat,
                lng: store.getters.frequencySearchData.lng,
                f1: "",
                f2: "",
                bw: store.getters.frequencySearchData.bw
            })
            this.search()
        }

        get searchLabel(): string | undefined {
            return this.$t("App.SearchButton.Label")
        }

        get searchValidationErrorMessage() {
            return store.getters.frequencySearcValidationMessage != null && store.getters.frequencySearcValidationMessage.length > 0 ? this.$t(store.getters.frequencySearcValidationMessage) : null
        }

        async search(): Promise<void> {
            // haku annettujen taajuuksien perusteella
            store.dispatch("setLoading", { name: this.pageName, value: true })
            store.dispatch("setFrequencySearchData", {
                lat: store.getters.frequencySearchData.lat,
                lng: store.getters.frequencySearchData.lng,
                f1: store.getters.frequencySearchData.f1,
                f2: store.getters.frequencySearchData.f2,
                bw: store.getters.frequencySearchData.bw
            })
            this.radiomikrofonitaajuudetService.availableFrequenciesSearch()

            store.dispatch("setLoading", { name: this.pageName, value: false })
        }

        get inputAriaLabel() {
            return this.label
        }

        validateInput() {
            this.frequencies = this.frequencies.replace(/[^\d\s.,—–-]/g, "")
            this.frequencies = this.cleanInput(this.frequencies, false)
            // frequency values that will be used in api call
            const delimeters = /[—–-]/
            const frequencyLimitValues = this.cleanInput(this.frequencies, true).split(delimeters)
            let f1
            let f2

            if (frequencyLimitValues.length === 1) {
                f1 = frequencyLimitValues[0].replace(",", ".")
                f2 = ""

                store.dispatch("setFrequencySearchData", {
                    lat: store.getters.frequencySearchData.lat,
                    lng: store.getters.frequencySearchData.lng,
                    f1,
                    f2,
                    bw: store.getters.frequencySearchData.bw
                })
            }
            else if (frequencyLimitValues.length === 2) {
                f1 = (frequencyLimitValues[0].replace(",", ".") || "min")
                f2 = (frequencyLimitValues[1].replace(",", ".") || "max")

                store.dispatch("setFrequencySearchData", {
                    lat: store.getters.frequencySearchData.lat,
                    lng: store.getters.frequencySearchData.lng,
                    f1,
                    f2,
                    bw: store.getters.frequencySearchData.bw
                })
            }
        }

        cleanInput(str, trimVal) {
            const parts = str.split("-")
            let cleanedValue = ""

            for (let i = 0; i < parts.length; i++) {
                const digits = parts[i].match(/\d/g)
                if (digits) {
                    const firstDotIndex = parts[i].indexOf(".")
                    const firstCommaIndex = parts[i].indexOf(",")
                    let firstDotOrCommaIndex

                    if (firstDotIndex < firstCommaIndex && firstDotIndex >= 0) {
                        firstDotOrCommaIndex = firstDotIndex
                    }
                    else {
                        firstDotOrCommaIndex = firstCommaIndex
                    }

                    if (firstDotOrCommaIndex !== -1) {
                        parts[i] = parts[i].substring(0, firstDotOrCommaIndex + 1) + parts[i].substring(firstDotOrCommaIndex + 1).replace(/[,.]/g, "")
                    }
                }

                // spaces will not be removed when cleaning the visible text input value
                if (trimVal) {
                    parts[i] = parts[i].replace(/\s/g, "")
                }
            }

            if (parts.length > 0) {
                cleanedValue = parts.join("-")
            }
            else {
                cleanedValue = parts.join()
            }

            return cleanedValue
        }
    }
</script>

<style scoped>
    .legendelement {
        font-size: 1.125rem;
    }

    .search-group input.search {
        width: unset;
        flex-grow: 1;
        height: 3.125rem;
        overflow: hidden;
        padding-right: 2.1rem;
        resize: none;
    }

        .search-group input.search.error {
            color: #9E003B;
            border-color: #9E003B;
            background-color: rgba(158,0,59,.05);
        }

    .closebutton {
        background: none;
        border: none;
        margin: 0;
        padding: 0;
        cursor: pointer;
        font-weight: 600;
        position: absolute;
        right: 70px;
        top: 12px;
    }

    .error-label {
        color: #DB0008;
    }
</style>
